import { useState, useEffect } from "react";
import { Report, ReportParameter } from "../../../types/reportTypes";
import { fetchApiData } from "../serviceFile";
import moment from "moment";

interface FormValues {
  [key: string]: any;
}

interface Errors {
  [key: string]: string;
}

interface PopupState {
  [key: string]: {
    data: any[];
    selected: any | null;
    total: number;
    isOpen: boolean;
    pagination: { pageIndex: number; pageSize: number };
    searchQuery: string;
    rowSelection: { [key: string]: boolean };
  };
}

// Helper function to safely get nested values from objects
const getNestedValue = (obj: any, path: string): any => {
  return path.split(".").reduce((current, key) => current?.[key], obj);
};

export const useReportForm = (selectedReport: Report | null) => {
  const [formValues, setFormValues] = useState<FormValues>({});
  const [errors, setErrors] = useState<Errors>({});
  const [popupState, setPopupState] = useState<PopupState>({});
  const [dropdownOptions, setDropdownOptions] = useState<{
    [key: string]: any[];
  }>({});
  const [loadingFields, setLoadingFields] = useState<{
    [key: string]: boolean;
  }>({});

  const validateField = (param: ReportParameter, value: any): string => {
    if (param.required) {
      if (!value) return `${param.label} is required`;
      if (param.multiSelect && Array.isArray(value) && value.length === 0) {
        return `${param.label} is required`;
      }
    }

    if (param.category === "datepicker" && value) {
      const date = moment(value, param.validate?.format || "YYYY-MM-DD");
      if (
        !date.isValid() ||
        (param.validate?.format && date.format(param.validate.format) !== value)
      ) {
        return `Invalid date format. Use ${param.validate?.format}`;
      }
    }

    if (param.category === "timepicker" && value) {
      if (!moment(value, "HH:mm").isValid())
        return `Invalid time format. Use HH:mm`;
    }

    if (param.category === "number" && value && isNaN(Number(value))) {
      return `Must be a valid number`;
    }

    if (param.category === "text" && value && typeof value !== "string") {
      return `Must be a valid text`;
    }

    return "";
  };

  const handleInputChange = (paramLabel: string, value: any) => {
    setFormValues((prev) => ({ ...prev, [paramLabel]: value }));
    setErrors((prev) => ({ ...prev, [paramLabel]: "" }));
  };

  const handleBlur = (paramLabel: string) => {
    if (!selectedReport) return;
    const param = selectedReport.parameters.find((p) => p.label === paramLabel);
    if (param)
      setErrors((prev) => ({
        ...prev,
        [paramLabel]: validateField(param, formValues[paramLabel]),
      }));
  };

  const clearForm = () => {
    setFormValues({});
    setErrors({});
    setDropdownOptions({});
    setPopupState({});
    setLoadingFields({});
  };

  // Load data for select fields
  useEffect(() => {
    if (!selectedReport) return;

    const loadData = async () => {
      const selectParams =
        selectedReport.parameters?.filter(
          (p) => p.category === "select" && p.apiEndpoint?.apiUrl
        ) || [];

      await Promise.all(
        selectParams.map(async (param) => {
          setLoadingFields((prev) => ({ ...prev, [param.label]: true }));
          try {
            const queryParams = param.apiEndpoint?.QueryParams?.reduce(
              (acc: Record<string, any>, key: string) => {
                if (key === "limit")
                  acc[key] =
                    popupState[param.label]?.pagination?.pageSize || 10;
                if (key === "page")
                  acc[key] =
                    (popupState[param.label]?.pagination?.pageIndex || 0) + 1;
                if (key === "search")
                  acc[key] = popupState[param.label]?.searchQuery || "";
                if (key === "type")
                  acc[key] = param.apiEndpoint?.apiUrl.includes("client")
                    ? "client_list"
                    : undefined;
                return acc;
              },
              {}
            );

            const response = await fetchApiData(
              param.apiEndpoint!.apiUrl,
              queryParams
            );

            let options: any[] = [];
            if (param.popup) {
              const data = param.apiEndpoint!.responseData
                ? getNestedValue(response, param.apiEndpoint!.responseData) ||
                  []
                : response.data || [];

              options = data.map((item: any) => ({
                value: item[param.apiEndpoint!.value],
                label: param.apiEndpoint!.attributes?.length
                  ? param
                      .apiEndpoint!.attributes.map(
                        (attr: any) => item[attr.value]
                      )
                      .join(" ")
                  : item[param.apiEndpoint!.value],
              }));
              const total = param.apiEndpoint!.count
                ? getNestedValue(response, param.apiEndpoint!.count) || 0
                : 0;
              setPopupState((prev: any) => ({
                ...prev,
                [param.label]: {
                  data,
                  selected: prev[param.label]?.selected || null,
                  total,
                  isOpen: prev[param.label]?.isOpen || false,
                  pagination: prev[param.label]?.pagination || {
                    pageIndex: 0,
                    pageSize: 10,
                  },
                  searchQuery: prev[param.label]?.searchQuery || "",
                  rowSelection: prev[param.label]?.rowSelection || {},
                },
              }));
            } else {
              let data: any[] = [];

              // Use responseData if defined (e.g. "data.customerList")
              if (param.apiEndpoint!.responseData) {
                data =
                  getNestedValue(response, param.apiEndpoint!.responseData) ||
                  [];
              }
              // Special case: /option endpoint
              else if (param.apiEndpoint!.apiUrl.includes("/option")) {
                data = response.data || [];
              }

              if (param.apiEndpoint!.apiUrl.includes("/option")) {
                options =
                  data[0]?.options?.map((item: any) => ({
                    value: item.value ?? "",
                    label: item.label ?? "",
                  })) || [];
              } else {
                options = data?.map((item: any) => {
                  console.log(data, "data");
                  const value = item[param.apiEndpoint!.value];

                  let label = "";
                  if (param.apiEndpoint!.attributes?.length) {
                    label = param
                      .apiEndpoint!.attributes.map(
                        (attr: any) => item[attr.label]
                      )
                      .filter(Boolean)
                      .join(" ");
                  } else if (item.label !== undefined) {
                    label = item.label;
                  } else {
                    label = String(value);
                  }

                  return { value, label };
                });
              }
              setDropdownOptions((prev) => ({
                ...prev,
                [param.label]: options,
              }));
            }
          } catch (err) {
            console.error(`Failed to load options for ${param.label}:`, err);
            setErrors((prev) => ({
              ...prev,
              [param.label]: `Failed to load ${param.label} options`,
            }));
          } finally {
            setLoadingFields((prev) => ({ ...prev, [param.label]: false }));
          }
        })
      );
    };

    loadData();
  }, [
    selectedReport,
    ...Object.keys(popupState).map(
      (key) => popupState[key].pagination?.pageIndex
    ),
    ...Object.keys(popupState).map(
      (key) => popupState[key].pagination?.pageSize
    ),
    ...Object.keys(popupState).map((key) => popupState[key].searchQuery),
  ]);

  return {
    formValues,
    errors,
    popupState,
    dropdownOptions,
    loadingFields,
    handleInputChange,
    handleBlur,
    clearForm,
    validateField,
    setPopupState,
  };
};
